import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { addDays } from "@torre/work-hours";
import {
  fetchClients,
  fetchDeliveries,
  fetchRuns,
  runDailyReport,
  runWeeklyReport,
  type DeliveryRow,
  type RunRow,
  type WorkHoursClientRow,
} from "../services/workHoursService";
import { dateTimeLabel, localeOf, today } from "../lib/heatmap";
import { describeRequestFailure, failureMessage, type FailureKeys, type FailureMessage } from "../lib/requestFailure";
import { DELIVERY_STATUS_TONES, RUN_STATUS_TONES, buildThemeClasses, inputClass } from "../theme";

// ---------------------------------------------------------------------------
// Reportes (#538 / #539): bitácora de entregas por canal con vista previa del
// mensaje, disparo manual del diario y del semanal, y corridas del agente
// nocturno (#535). Sin credenciales de correo la entrega queda en «vista previa»;
// WhatsApp aparece como «sin proveedor» con su texto corto ya armado.
//
// Dos cosas que esta pantalla NO puede hacer, porque aquí se manda correo de
// verdad a clientes:
//  · Inventar la causa de un fallo. Cualquier tropiezo al CARGAR se leía como
//    «solo los administradores ven los reportes»; un corte de red no es un
//    problema de permisos y solo un 403 puede decir eso.
//  · Negar un envío que no vio terminar. Si el disparo se queda sin respuesta,
//    los correos pueden haber salido igual: se relee la bitácora de ENTREGAS
//    antes de opinar y se dice que no se pudo confirmar, sin invitar a repetir
//    el envío a ciegas (repetir = el cliente recibe el correo dos veces).
// ---------------------------------------------------------------------------

type Kind = "daily" | "weekly";

/** Fallo al CARGAR la bitácora de entregas y las corridas. */
const LOAD_KEYS: FailureKeys = {
  failed: "workHours.reports.loadError",
  permission: "workHours.reports.adminOnly",
  auth: "workHours.reports.loadAuth",
  unconfirmed: "workHours.reports.loadTimeout",
  server: "workHours.reports.loadErrorServer",
};

/** Fallo al DISPARAR un reporte, que es lo que manda los correos. */
const RUN_KEYS: FailureKeys = {
  failed: "workHours.reports.runError",
  permission: "workHours.reports.runForbidden",
  auth: "workHours.reports.runAuth",
  unconfirmed: "workHours.reports.runUnconfirmed",
  server: "workHours.reports.runErrorServer",
};

export default function ReportsSection() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const card = `rounded-2xl border p-4 ${tc.cardBg} ${tc.border}`;
  const locale = localeOf(i18n?.language);

  const [deliveries, setDeliveries] = useState<DeliveryRow[]>([]);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [clients, setClients] = useState<WorkHoursClientRow[]>([]);
  const [clientsFailed, setClientsFailed] = useState(false);
  const [selected, setSelected] = useState<DeliveryRow | null>(null);
  const [date, setDate] = useState(today());
  const [busy, setBusy] = useState<Kind | null>(null);
  const [confirming, setConfirming] = useState<Kind | null>(null);
  // Clave i18n, no texto: así `load` no depende de `t` (que react-i18next
  // reemplaza al cargar un namespace o al cambiar de idioma) y no se recarga sola.
  const [errorKey, setErrorKey] = useState<FailureMessage | null>(null);
  // El fallo del DISPARO vive aparte del fallo de la CARGA: tras un corte por
  // tiempo se relee la bitácora de entregas, y esa relectura limpia su propio
  // error —el aviso del disparo, que es el único que explica lo que pasó con
  // los correos, tiene que sobrevivir a ella.
  const [runError, setRunError] = useState<FailureMessage | null>(null);

  const load = useCallback(async () => {
    try {
      const [d, r] = await Promise.all([fetchDeliveries(), fetchRuns()]);
      setDeliveries(d);
      setRuns(r);
      setErrorKey(null);
    } catch (e) {
      // Un fallo de red o del servidor NO es un problema de permisos: solo un
      // 403 puede decir «esta sección es de administradores».
      setErrorKey(failureMessage(describeRequestFailure(e), LOAD_KEYS));
    }
    // Los clientes solo alimentan la confirmación: si fallan, no rompen la
    // vista. Pero una lista que no llegó NO es «no hay clientes activos»: se
    // marca como desconocida para no prometer que nadie recibirá el correo.
    try {
      setClients(await fetchClients());
      setClientsFailed(false);
    } catch {
      setClients([]);
      setClientsFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // El semanal cubre los 7 días que terminan hoy (mismo criterio que el backend).
  const weekTo = today();
  const weekFrom = addDays(weekTo, -6);
  const activeClients = clients.filter((c) => c.active !== false);

  const trigger = async (kind: Kind) => {
    setConfirming(null);
    setBusy(kind);
    setRunError(null);
    try {
      if (kind === "daily") await runDailyReport(date);
      else await runWeeklyReport();
      await load();
    } catch (e) {
      const failure = describeRequestFailure(e);
      // Sin respuesta no se puede negar el envío: el servidor pudo terminar de
      // mandar los correos igual. Se relee la bitácora de ENTREGAS antes de
      // opinar, para que la entrega que sí salió esté a la vista junto al
      // aviso — y nadie vuelva a pulsar mandando el correo dos veces.
      if (failure.kind === "unconfirmed") await load();
      setRunError(failureMessage(failure, RUN_KEYS));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="w-full max-w-7xl overflow-y-auto p-4 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className={`text-xl font-bold ${tc.text}`}>{t("workHours.menu.reports")}</h1>
          <p className={`text-sm ${tc.textSecondary}`}>{t("workHours.reports.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" aria-label={t("workHours.reports.date")} value={date} max={today()} onChange={(e) => setDate(e.target.value)} className={inputClass(isDarkMode)} />
          <button
            type="button"
            onClick={() => setConfirming("daily")}
            disabled={busy !== null || confirming !== null}
            className="rounded-lg bg-violet-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
          >
            <i className="ri-mail-send-line mr-1" />
            {busy === "daily" ? t("workHours.saving") : t("workHours.reports.runDaily")}
          </button>
          <button
            type="button"
            onClick={() => setConfirming("weekly")}
            disabled={busy !== null || confirming !== null}
            className={`rounded-lg border px-3 py-2 text-xs font-medium ${tc.border} ${tc.text} disabled:opacity-50`}
          >
            <i className="ri-calendar-event-line mr-1" />
            {busy === "weekly" ? t("workHours.saving") : t("workHours.reports.runWeekly")}
          </button>
        </div>
      </div>
      {errorKey && (
        <div data-testid="reports-error" className="rounded-lg bg-red-500/10 p-3 text-sm text-red-500">
          <p>{t(errorKey.key, errorKey.opts)}</p>
          <button type="button" onClick={() => void load()} className="mt-2 rounded-md border border-current px-2 py-1 text-[11px] font-medium opacity-90">
            <i className="ri-refresh-line mr-1" />
            {t("workHours.retry")}
          </button>
        </div>
      )}
      {/* Fallo del disparo. Ámbar y no rojo cuando el envío quedó sin
          confirmar: no se sabe que haya fallado nada, y lo único que se ofrece
          es RECARGAR las entregas, nunca volver a enviar. */}
      {runError && (
        <div
          data-testid="reports-run-error"
          className={`rounded-lg p-3 text-sm ${
            runError.key === "workHours.reports.runUnconfirmed" ? "bg-amber-500/10 text-amber-600" : "bg-red-500/10 text-red-500"
          }`}
        >
          <p>{t(runError.key, runError.opts)}</p>
          <button type="button" onClick={() => void load()} className="mt-2 rounded-md border border-current px-2 py-1 text-[11px] font-medium opacity-90">
            <i className="ri-refresh-line mr-1" />
            {t("workHours.reports.reloadDeliveries")}
          </button>
        </div>
      )}

      {/* Confirmación antes de disparar: el envío manda correo de verdad, y dos
          clics mandaban dos correos al mismo cliente sin aviso. */}
      {confirming && (
        <div data-testid="report-confirm" className={`${card} border-amber-500/50`}>
          <p className={`text-sm font-semibold ${tc.text}`}>
            <i className="ri-alert-line mr-1 text-amber-500" />
            {t("workHours.reports.confirmTitle")}
          </p>
          <p className={`mt-1 text-sm ${tc.textSecondary}`}>
            {confirming === "daily"
              ? t("workHours.reports.confirmDaily", { date })
              : t("workHours.reports.confirmWeekly", { from: weekFrom, to: weekTo, count: activeClients.length })}
          </p>
          {confirming === "weekly" && (
            <p className={`mt-1 text-xs ${tc.textSecondary}`} data-testid="report-confirm-recipients">
              {clientsFailed
                ? t("workHours.reports.confirmClientsUnknown")
                : activeClients.length === 0
                  ? t("workHours.reports.confirmNoClients")
                  : `${t("workHours.reports.recipients")}: ${activeClients.map((c) => `${c.name} (${c.emails.join(", ") || "—"})`).join(" · ")}`}
            </p>
          )}
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => void trigger(confirming)}
              disabled={busy !== null}
              className="rounded-lg bg-violet-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
            >
              {t("workHours.reports.confirmSend")}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(null)}
              className={`rounded-lg border px-3 py-2 text-xs font-medium ${tc.border} ${tc.text}`}
            >
              {t("workHours.cancel")}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
        <div className={`${card} xl:col-span-3`}>
          <p className={`mb-2 text-sm font-semibold ${tc.text}`}>{t("workHours.reports.deliveries")}</p>
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left text-xs ${tc.textSecondary}`}>
                <th className="py-1">{t("workHours.reports.when")}</th>
                <th className="py-1">{t("workHours.reports.type")}</th>
                <th className="py-1">{t("workHours.reports.channel")}</th>
                <th className="py-1">{t("workHours.reports.recipients")}</th>
                <th className="py-1">{t("workHours.reports.status")}</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => setSelected(d)}
                  className={`cursor-pointer border-t ${tc.border} ${tc.text} ${selected?.id === d.id ? (isDarkMode ? "bg-white/5" : "bg-violet-50") : ""}`}
                >
                  <td className="py-1.5 text-xs">{dateTimeLabel(d.createdAt, locale)}</td>
                  <td className="py-1.5 text-xs">{t(`workHours.reports.types.${d.type}`)}</td>
                  <td className="py-1.5 text-xs">
                    <i className={d.channel === "email" ? "ri-mail-line" : "ri-whatsapp-line"} /> {d.channel}
                  </td>
                  <td className="py-1.5 text-xs">{d.recipients.join(", ") || "—"}</td>
                  <td className="py-1.5">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] ${DELIVERY_STATUS_TONES[d.status]}`}>{t(`workHours.reports.statuses.${d.status}`)}</span>
                  </td>
                </tr>
              ))}
              {deliveries.length === 0 && (
                <tr>
                  <td colSpan={5} className={`py-3 text-center text-xs ${tc.textSecondary}`}>
                    {t("workHours.reports.empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className={`${card} xl:col-span-2`}>
          <p className={`mb-2 text-sm font-semibold ${tc.text}`}>{t("workHours.reports.preview")}</p>
          {!selected && <p className={`text-xs ${tc.textSecondary}`}>{t("workHours.reports.previewHint")}</p>}
          {selected && (
            <div data-testid="delivery-preview">
              <p className={`text-xs font-semibold ${tc.text}`}>{selected.subject}</p>
              {selected.error && <p className="mt-1 text-xs text-red-500">{selected.error}</p>}
              {selected.channel === "whatsapp" ? (
                <pre className={`mt-2 whitespace-pre-wrap rounded-lg p-3 text-xs ${isDarkMode ? "bg-black/40 text-gray-200" : "bg-emerald-50 text-gray-800"}`}>{selected.shortText}</pre>
              ) : (
                <iframe title="email-preview" sandbox="" srcDoc={selected.html} className="mt-2 h-[420px] w-full rounded-lg border bg-white" />
              )}
            </div>
          )}
        </div>
      </div>

      <div className={card}>
        <p className={`mb-2 text-sm font-semibold ${tc.text}`}>{t("workHours.reports.runs")}</p>
        <table className="w-full text-sm">
          <thead>
            <tr className={`text-left text-xs ${tc.textSecondary}`}>
              <th className="py-1">{t("workHours.reports.date")}</th>
              <th className="py-1">{t("workHours.reports.status")}</th>
              <th className="py-1 text-right">Repos</th>
              <th className="py-1 text-right">{t("workHours.kpi.commits")}</th>
              <th className="py-1">Host</th>
              <th className="py-1">{t("workHours.reports.errors")}</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className={`border-t ${tc.border} ${tc.text}`}>
                <td className="py-1.5 text-xs">{r.date}</td>
                <td className="py-1.5">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] ${RUN_STATUS_TONES[r.status]}`}>{r.status}</span>
                </td>
                <td className="py-1.5 text-right">{r.repos}</td>
                <td className="py-1.5 text-right">{r.commits}</td>
                <td className="py-1.5 text-xs">{r.host ?? "—"}</td>
                <td className="py-1.5 text-xs text-red-500">{r.errors.slice(0, 2).join("; ")}</td>
              </tr>
            ))}
            {runs.length === 0 && (
              <tr>
                <td colSpan={6} className={`py-3 text-center text-xs ${tc.textSecondary}`}>
                  {t("workHours.reports.noRuns")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
